import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/efs/PageHeader';
import { TableToolbar } from '@/components/efs/TableToolbar';
import { ApprovalStatusBadge } from '@/components/efs/ApprovalStatusBadge';
import { MasterTable, type MasterTableColumn } from '@/components/efs/MasterTable';
import { MasterExportButton } from '@/components/efs/MasterExportButton';
import { DestinationsFormDialog } from './DestinationsFormDialog';
import { DestinationsRowActions } from './DestinationsRowActions';
import { useDestinationsList } from './useDestinations';
import type { Destination } from './types';

const DEFAULT_PER_PAGE = 50;
const PAGE_SIZE_OPTIONS = [50, 100];

export const DestinationsListPage = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [createOpen, setCreateOpen] = useState(false);

  // Audit-issue navigation intents may carry a search/highlight query
  // (spec-13 §6.2) pointing at the affected master row.
  const [searchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const highlightQ = searchParams.get('q');
  const query = useDestinationsList({ page, per_page: perPage });

  const columns = useMemo<MasterTableColumn<Destination>[]>(
    () => [
      {
        key: 'japanese_name',
        header: t('efs.masters.fields.japanese_name', { ns: 'masters' }),
        cell: (row) => row.company_display_name ?? row.japanese_name ?? row.company_id,
      },
      {
        key: 'destination_type',
        header: t('efs.masters.fields.destination_type', { ns: 'masters' }),
        cell: (row) =>
          t(`efs.masters.destination_type.${row.destination_type}`, {
            ns: 'masters',
            defaultValue: row.destination_type,
          }),
      },
      {
        key: 'certificate_expires_at',
        header: t('efs.masters.fields.certificate_expires_at', { ns: 'masters' }),
        cell: (row) => row.certificate_expires_at ?? '—',
      },
      {
        key: 'approval_status',
        header: t('efs.masters.fields.approval_status', { ns: 'masters' }),
        cell: (row) => <ApprovalStatusBadge status={row.approval_status} />,
      },
      {
        key: 'lifecycle',
        header: t('efs.masters.fields.active', { ns: 'masters' }),
        cell: (row) => {
          if (row.approval_status === 'retired') {
            return (
              <Badge variant="outline">
                {t('efs.common.status.retired', { defaultValue: '無効' })}
              </Badge>
            );
          }
          if (row.approval_status !== 'approved') {
            return <span className="text-xs text-efs-text-muted">—</span>;
          }
          return (
            <Badge variant="default">
              {t('efs.common.status.approved', { defaultValue: '有効' })}
            </Badge>
          );
        },
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        cell: (row) => <DestinationsRowActions row={row} />,
      },
    ],
    [t],
  );

  const rows = query.data?.rows ?? [];
  const total = query.data?.meta?.total ?? 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.masters.destination.title', { ns: 'masters' })}
        subtitle={t('efs.masters.destination.subtitle', { ns: 'masters' })}
        count={total}
        actions={
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" />
            {t('efs.masters.actions.create', { ns: 'masters' })}
          </Button>
        }
      />

      <TableToolbar
        count={t('efs.masters.toolbar.count', { ns: 'masters', count: total })}
        actions={<MasterExportButton kind="destinations" />}
      />

      <MasterTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.destination_company_id}
        rowProps={(row) =>
          (!!highlightId && row.destination_company_id === highlightId) ||
          (!!highlightQ && row.company_display_name === highlightQ)
            ? { className: 'bg-efs-bg-subtle ring-1 ring-efs-accent' }
            : undefined
        }
        isLoading={query.isLoading}
        pagination={{
          page,
          perPage,
          total,
          onPageChange: setPage,
          onPerPageChange: (nextPerPage) => {
            setPage(1);
            setPerPage(nextPerPage);
          },
          pageSizeOptions: PAGE_SIZE_OPTIONS,
        }}
      />

      <DestinationsFormDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
};

export default DestinationsListPage;
